import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Typography,
  Table,
  Tag,
  Progress,
  Tabs,
  List,
  Avatar,
  DatePicker,
  Select,
  Input,
  Modal,
  Form,
  message,
  Tooltip,
  Badge,
  InputNumber,
  Divider,
  Checkbox
} from 'antd';
import {
  DollarOutlined,
  BankOutlined,
  FileTextOutlined,
  CalculatorOutlined,
  PieChartOutlined,
  BarChartOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CreditCardOutlined,
  MoneyCollectOutlined,
  AccountBookOutlined,
  SnippetsOutlined,
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  DownloadOutlined,
  PrinterOutlined,
  CalendarOutlined,
  BookOutlined,
  ShoppingOutlined,
  WalletOutlined,
  ReconciliationOutlined,
  SettingOutlined
} from '@ant-design/icons';

// Import our new accounting components
import JournalEntryManagement from '../components/JournalEntry';
import InvoiceManagement from '../components/InvoiceManagement';
import PaymentManagement from '../components/PaymentManagement';
import BankReconciliation from '../components/BankReconciliation';
import AccountingSettings from '../components/AccountingSettings';
import JournalEntries from '../components/JournalEntries';
import GeneralLedger from '../components/GeneralLedger';
import type { ColumnsType } from 'antd/es/table';
import accountService, { type Account as ApiAccount } from '@/services/accountService';
import journalService, { type Journal } from '@/services/journalService';
import { useAuthStore } from '@/stores/authStore';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;
const { TabPane } = Tabs;

interface Account {
  id: string;
  code: string;
  name: string;
  accountType: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'REVENUE' | 'EXPENSE';
  parentId?: string;
  balance?: number;
  deprecated: boolean;
  reconcile: boolean;
  note?: string;
  companyId: string;
  createdAt?: string;
  updatedAt?: string;
}

interface Transaction {
  id: string;
  date: string;
  reference: string;
  description: string;
  entries: JournalEntry[];
  totalDebit: number;
  totalCredit: number;
  status: 'draft' | 'posted' | 'cancelled';
  createdBy: string;
}

interface JournalEntry {
  id: string;
  accountId: string;
  accountName: string;
  debit: number;
  credit: number;
  description?: string;
}

interface FinancialData {
  totalRevenue: number;
  totalExpenses: number;
  netIncome: number;
  totalAssets: number;
  totalLiabilities: number;
  equity: number;
  cashFlow: number;
  accountsReceivable: number;
  accountsPayable: number;
  accounts: Account[];
  recentTransactions: Transaction[];
  profitLossData: Array<{
    month: string;
    revenue: number;
    expenses: number;
    profit: number;
  }>;
}

const AccountingOverview: React.FC = () => {
  const location = useLocation();
  const { user } = useAuthStore();

  // Function to get active tab from route
  const getActiveTabFromRoute = () => {
    const path = location.pathname;
    if (path.includes('/accounting/chart-of-accounts')) return 'accounts';
    if (path.includes('/accounting/journal-entries')) return 'journal';
    if (path.includes('/accounting/invoices')) return 'invoices';
    if (path.includes('/accounting/reports')) return 'reports';
    return 'overview';
  };

  const [financialData, setFinancialData] = useState<FinancialData>({
    totalRevenue: 0,
    totalExpenses: 0,
    netIncome: 0,
    totalAssets: 0,
    totalLiabilities: 0,
    equity: 0,
    cashFlow: 0,
    accountsReceivable: 0,
    accountsPayable: 0,
    accounts: [],
    recentTransactions: [],
    profitLossData: []
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(getActiveTabFromRoute());
  const [transactionModalVisible, setTransactionModalVisible] = useState(false);
  const [accountModalVisible, setAccountModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [accountFilter, setAccountFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [viewDetailsModalVisible, setViewDetailsModalVisible] = useState(false);
  const [viewingAccount, setViewingAccount] = useState<Account | null>(null);
  const [journals, setJournals] = useState<Journal[]>([]);

  // Update active tab when route changes
  useEffect(() => {
    setActiveTab(getActiveTabFromRoute());
  }, [location.pathname]);

  // Fetch accounts from API
  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const accounts = await accountService.getAccounts();

      // Transform API data to match component interface
      const transformedAccounts: Account[] = accounts.map((acc: ApiAccount) => ({
        id: acc.id,
        code: acc.code,
        name: acc.name,
        accountType: acc.accountType,
        parentId: acc.parentId,
        balance: 0, // Balance would need to be fetched separately or included in response
        deprecated: acc.deprecated,
        reconcile: acc.reconcile,
        note: acc.note,
        companyId: acc.companyId,
        createdAt: acc.createdAt,
        updatedAt: acc.updatedAt
      }));

      // Calculate financial metrics from accounts
      // Note: This is a simplified calculation. In production, you'd have dedicated endpoints for these metrics
      const revenue = 0; // Would be calculated from revenue accounts
      const expenses = 0; // Would be calculated from expense accounts

      setFinancialData(prev => ({
        ...prev,
        accounts: transformedAccounts,
        totalRevenue: revenue,
        totalExpenses: expenses,
        netIncome: revenue - expenses
      }));
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch accounts');
      console.error('Error fetching accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch journals from API
  const fetchJournals = async () => {
    try {
      const journalsData = await journalService.getJournals();
      setJournals(journalsData);
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch journals');
    }
  };

  useEffect(() => {
    fetchAccounts();
    fetchJournals();
  }, []);

  const getAccountTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      ASSET: 'green',
      LIABILITY: 'red',
      EQUITY: 'blue',
      REVENUE: 'purple',
      EXPENSE: 'orange',
      // Lowercase variants for backward compatibility
      asset: 'green',
      liability: 'red',
      equity: 'blue',
      revenue: 'purple',
      expense: 'orange'
    };
    return colors[type] || 'default';
  };

  // Handle creating/updating accounts
  const handleAccountSubmit = async (values: any) => {
    if (!user?.companyId) {
      message.error('Company ID not found. Please log in again.');
      return;
    }

    setLoading(true);
    try {
      if (selectedAccount) {
        // Update existing account
        await accountService.updateAccount(selectedAccount.id, values);
        message.success('Account updated successfully');
      } else {
        // Create new account
        await accountService.createAccount({
          ...values,
          companyId: user.companyId
        });
        message.success('Account created successfully');
      }
      setAccountModalVisible(false);
      form.resetFields();
      setSelectedAccount(null);
      fetchAccounts();
    } catch (error: any) {
      message.error(error.message || 'Failed to save account');
    } finally {
      setLoading(false);
    }
  };

  // Handle account edit
  const handleEditAccount = (account: Account) => {
    setSelectedAccount(account);
    form.setFieldsValue({
      code: account.code,
      name: account.name,
      accountType: account.accountType,
      parentId: account.parentId,
      reconcile: account.reconcile,
      note: account.note
    });
    setAccountModalVisible(true);
  };

  // Handle account delete
  const handleDeleteAccount = async (accountId: string) => {
    try {
      await accountService.deprecateAccount(accountId);
      message.success('Account deprecated successfully');
      fetchAccounts();
    } catch (error: any) {
      message.error(error.message || 'Failed to deprecate account');
    }
  };

  // Handle view account details
  const handleViewDetails = (account: Account) => {
    setViewingAccount(account);
    setViewDetailsModalVisible(true);
  };

  // Filter accounts based on search query and type filter
  const filteredAccounts = financialData.accounts.filter(account => {
    // Filter by type
    if (accountFilter !== 'all') {
      if (account.accountType.toLowerCase() !== accountFilter.toLowerCase()) {
        return false;
      }
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesCode = account.code.toLowerCase().includes(query);
      const matchesName = account.name.toLowerCase().includes(query);
      return matchesCode || matchesName;
    }

    return true;
  });

  const getTransactionStatusColor = (status: string) => {
    const colors = {
      draft: 'default',
      posted: 'green',
      cancelled: 'red'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const accountColumns: ColumnsType<Account> = [
    {
      title: 'Account Code',
      dataIndex: 'code',
      key: 'code',
      render: (code) => <Text strong>{code}</Text>
    },
    {
      title: 'Account Name',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <Text>{name}</Text>
    },
    {
      title: 'Type',
      dataIndex: 'accountType',
      key: 'accountType',
      render: (accountType) => (
        <Tag color={getAccountTypeColor(accountType)}>
          {accountType}
        </Tag>
      )
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      render: (balance = 0, record) => (
        <Text strong style={{
          color: record.accountType === 'LIABILITY' || record.accountType === 'EXPENSE' ?
                 (balance > 0 ? '#ff4d4f' : '#52c41a') :
                 (balance > 0 ? '#52c41a' : '#ff4d4f')
        }}>
          ${Math.abs(balance).toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => (a.balance || 0) - (b.balance || 0)
    },
    {
      title: 'Status',
      dataIndex: 'deprecated',
      key: 'deprecated',
      render: (deprecated) => (
        <Tag color={!deprecated ? 'green' : 'red'}>
          {!deprecated ? 'ACTIVE' : 'DEPRECATED'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="link"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Account">
            <Button
              type="link"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditAccount(record)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const transactionColumns: ColumnsType<Transaction> = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      render: (reference) => <Text strong>{reference}</Text>
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description'
    },
    {
      title: 'Debit',
      dataIndex: 'totalDebit',
      key: 'totalDebit',
      render: (amount) => (
        <Text style={{ color: '#52c41a' }}>
          ${amount.toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => a.totalDebit - b.totalDebit
    },
    {
      title: 'Credit',
      dataIndex: 'totalCredit',
      key: 'totalCredit',
      render: (amount) => (
        <Text style={{ color: '#ff4d4f' }}>
          ${amount.toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => a.totalCredit - b.totalCredit
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getTransactionStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} size="small" />
          <Button type="link" icon={<EditOutlined />} size="small" disabled={record.status === 'posted'} />
          <Button type="link" icon={<PrinterOutlined />} size="small" />
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>
            <CalculatorOutlined style={{ marginRight: 8 }} />
            Accounting Overview
          </Title>
        </Col>
        <Col>
          <Space>
            <Button type="primary" icon={<PlusOutlined />}>
              New Transaction
            </Button>
            <Button icon={<FileTextOutlined />}>
              Generate Report
            </Button>
            <Button icon={<DownloadOutlined />}>
              Export Data
            </Button>
          </Space>
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Financial Overview" key="overview">
          {/* Key Financial Metrics */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Revenue"
                  value={financialData.totalRevenue}
                  prefix={<DollarOutlined />}
                  precision={0}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Expenses"
                  value={financialData.totalExpenses}
                  prefix={<DollarOutlined />}
                  precision={0}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Net Income"
                  value={financialData.netIncome}
                  prefix={<ArrowUpOutlined />}
                  precision={0}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Cash Flow"
                  value={financialData.cashFlow}
                  prefix={<MoneyCollectOutlined />}
                  precision={0}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Balance Sheet Summary */}
          <Row gutter={24} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={8}>
              <Card title="Assets" extra={<Button type="link">View Details</Button>}>
                <Statistic
                  value={financialData.totalAssets}
                  prefix="$"
                  precision={0}
                  valueStyle={{ fontSize: 24, color: '#52c41a' }}
                />
                <Divider />
                <div>
                  <div style={{ marginBottom: 8 }}>
                    <Text>Cash: </Text>
                    <Text strong>${financialData.cashFlow.toLocaleString()}</Text>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <Text>A/R: </Text>
                    <Text strong>${financialData.accountsReceivable.toLocaleString()}</Text>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card title="Liabilities" extra={<Button type="link">View Details</Button>}>
                <Statistic
                  value={financialData.totalLiabilities}
                  prefix="$"
                  precision={0}
                  valueStyle={{ fontSize: 24, color: '#ff4d4f' }}
                />
                <Divider />
                <div>
                  <div style={{ marginBottom: 8 }}>
                    <Text>A/P: </Text>
                    <Text strong>${financialData.accountsPayable.toLocaleString()}</Text>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card title="Equity" extra={<Button type="link">View Details</Button>}>
                <Statistic
                  value={financialData.equity}
                  prefix="$"
                  precision={0}
                  valueStyle={{ fontSize: 24, color: '#1890ff' }}
                />
                <Divider />
                <Progress 
                  percent={(financialData.equity / financialData.totalAssets) * 100}
                  strokeColor="#1890ff"
                  format={percent => `${percent?.toFixed(1)}%`}
                />
                <Text type="secondary">Equity Ratio</Text>
              </Card>
            </Col>
          </Row>

          {/* Recent Transactions */}
          <Card title="Recent Transactions" extra={<Button type="link">View All</Button>}>
            <Table
              dataSource={financialData.recentTransactions}
              columns={transactionColumns}
              pagination={false}
              size="small"
            />
          </Card>
        </TabPane>

        <TabPane tab="Chart of Accounts" key="accounts">
          <Card
            title="Chart of Accounts"
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedAccount(null);
                  form.resetFields();
                  setAccountModalVisible(true);
                }}
              >
                Add Account
              </Button>
            }
          >
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Search
                  placeholder="Search accounts by code or name..."
                  style={{ width: 300 }}
                  allowClear
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onSearch={(value) => setSearchQuery(value)}
                />
                <Select
                  placeholder="Filter by type"
                  style={{ width: 150 }}
                  value={accountFilter}
                  onChange={(value) => setAccountFilter(value)}
                >
                  <Select.Option value="all">All Types</Select.Option>
                  <Select.Option value="asset">Assets</Select.Option>
                  <Select.Option value="liability">Liabilities</Select.Option>
                  <Select.Option value="equity">Equity</Select.Option>
                  <Select.Option value="revenue">Revenue</Select.Option>
                  <Select.Option value="expense">Expenses</Select.Option>
                </Select>
              </Space>
            </div>
            <Table
              dataSource={filteredAccounts}
              columns={accountColumns}
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} accounts`
              }}
              rowKey="id"
            />
          </Card>
        </TabPane>

        <TabPane tab="Journal Entries" key="journal" icon={<BookOutlined />}>
          <JournalEntries journals={journals} accounts={financialData.accounts} />
        </TabPane>

        <TabPane tab="Financial Reports" key="reports">
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <Card title="Income Statement" extra={<Button icon={<DownloadOutlined />}>Export</Button>}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <Text strong>Revenue</Text>
                    <Text strong style={{ color: '#52c41a' }}>
                      ${financialData.totalRevenue.toLocaleString()}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <Text>Total Expenses</Text>
                    <Text style={{ color: '#ff4d4f' }}>
                      ${financialData.totalExpenses.toLocaleString()}
                    </Text>
                  </div>
                  <Divider />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text strong>Net Income</Text>
                    <Text strong style={{ 
                      color: financialData.netIncome > 0 ? '#52c41a' : '#ff4d4f',
                      fontSize: 16 
                    }}>
                      ${financialData.netIncome.toLocaleString()}
                    </Text>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Balance Sheet" extra={<Button icon={<DownloadOutlined />}>Export</Button>}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 12 }}>
                    <Text strong>Assets</Text>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Total Assets</Text>
                      <Text strong>${financialData.totalAssets.toLocaleString()}</Text>
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <Text strong>Liabilities</Text>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Total Liabilities</Text>
                      <Text>${financialData.totalLiabilities.toLocaleString()}</Text>
                    </div>
                  </div>
                  <div>
                    <Text strong>Equity</Text>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Total Equity</Text>
                      <Text strong>${financialData.equity.toLocaleString()}</Text>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          <Card title="Financial Analytics" style={{ marginTop: 24 }}>
            <Row gutter={24}>
              <Col span={12}>
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <PieChartOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                  <div style={{ marginTop: 16 }}>
                    <Text>Profit & Loss Chart</Text>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <BarChartOutlined style={{ fontSize: 48, color: '#52c41a' }} />
                  <div style={{ marginTop: 16 }}>
                    <Text>Cash Flow Trends</Text>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </TabPane>

        <TabPane tab="General Ledger" key="general-ledger" icon={<AccountBookOutlined />}>
          <GeneralLedger accounts={financialData.accounts} />
        </TabPane>

        <TabPane tab="Invoices" key="invoices" icon={<FileTextOutlined />}>
          <InvoiceManagement />
        </TabPane>

        <TabPane tab="Payments" key="payments" icon={<MoneyCollectOutlined />}>
          <PaymentManagement />
        </TabPane>

        <TabPane tab="Bank Reconciliation" key="bank-reconciliation" icon={<ReconciliationOutlined />}>
          <BankReconciliation />
        </TabPane>

        <TabPane tab="Settings" key="settings" icon={<SettingOutlined />}>
          <AccountingSettings />
        </TabPane>

        <TabPane tab="Tax Management" key="tax">
          <Card title="Tax Overview">
            <Row gutter={24}>
              <Col xs={24} lg={8}>
                <Card size="small" title="Sales Tax">
                  <Statistic
                    value={12500}
                    prefix="$"
                    suffix="collected"
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card size="small" title="Income Tax">
                  <Statistic
                    value={28650}
                    prefix="$"
                    suffix="owed"
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card size="small" title="Next Filing">
                  <div style={{ textAlign: 'center' }}>
                    <CalendarOutlined style={{ fontSize: 24, color: '#faad14' }} />
                    <div style={{ marginTop: 8 }}>
                      <Text strong>July 15, 2024</Text>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
          </Card>
        </TabPane>
      </Tabs>

      {/* Account Modal */}
      <Modal
        title={selectedAccount ? 'Edit Account' : 'Create Account'}
        open={accountModalVisible}
        onCancel={() => {
          setAccountModalVisible(false);
          form.resetFields();
          setSelectedAccount(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={loading}
        width={600}
      >
        <Form form={form} onFinish={handleAccountSubmit} layout="vertical">
          <Form.Item
            name="code"
            label="Account Code"
            rules={[{ required: true, message: 'Please enter account code' }]}
          >
            <Input placeholder="e.g., 1000, 2000, 3000" />
          </Form.Item>

          <Form.Item
            name="name"
            label="Account Name"
            rules={[{ required: true, message: 'Please enter account name' }]}
          >
            <Input placeholder="e.g., Assets, Cash, Accounts Receivable" />
          </Form.Item>

          <Form.Item
            name="accountType"
            label="Account Type"
            rules={[{ required: true, message: 'Please select account type' }]}
          >
            <Select placeholder="Select account type">
              <Select.Option value="ASSET">Asset</Select.Option>
              <Select.Option value="LIABILITY">Liability</Select.Option>
              <Select.Option value="EQUITY">Equity</Select.Option>
              <Select.Option value="REVENUE">Revenue</Select.Option>
              <Select.Option value="EXPENSE">Expense</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="parentId"
            label="Parent Account"
            tooltip="Select a parent account to create a sub-account"
          >
            <Select allowClear placeholder="Select parent account (optional)">
              {financialData.accounts
                .filter(a => !a.deprecated && !a.parentId)
                .map(a => (
                  <Select.Option key={a.id} value={a.id}>
                    {a.code} - {a.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="reconcile"
            valuePropName="checked"
            tooltip="Enable for bank accounts and accounts that need reconciliation"
          >
            <Checkbox>Reconcilable (for bank accounts)</Checkbox>
          </Form.Item>

          <Form.Item
            name="note"
            label="Notes"
          >
            <Input.TextArea
              rows={3}
              placeholder="Additional notes or description for this account"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* View Details Modal */}
      <Modal
        title="Account Details"
        open={viewDetailsModalVisible}
        onCancel={() => {
          setViewDetailsModalVisible(false);
          setViewingAccount(null);
        }}
        footer={[
          <Button key="close" onClick={() => setViewDetailsModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="edit"
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              if (viewingAccount) {
                handleEditAccount(viewingAccount);
                setViewDetailsModalVisible(false);
              }
            }}
          >
            Edit Account
          </Button>
        ]}
        width={600}
      >
        {viewingAccount && (
          <div style={{ padding: '16px 0' }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">Account Code</Text>
                  <div>
                    <Text strong style={{ fontSize: 16 }}>{viewingAccount.code}</Text>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">Account Name</Text>
                  <div>
                    <Text strong style={{ fontSize: 16 }}>{viewingAccount.name}</Text>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">Account Type</Text>
                  <div>
                    <Tag color={getAccountTypeColor(viewingAccount.accountType)} style={{ fontSize: 14 }}>
                      {viewingAccount.accountType}
                    </Tag>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">Status</Text>
                  <div>
                    <Tag color={!viewingAccount.deprecated ? 'green' : 'red'} style={{ fontSize: 14 }}>
                      {!viewingAccount.deprecated ? 'ACTIVE' : 'DEPRECATED'}
                    </Tag>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">Reconcilable</Text>
                  <div>
                    <Tag color={viewingAccount.reconcile ? 'blue' : 'default'} style={{ fontSize: 14 }}>
                      {viewingAccount.reconcile ? 'YES' : 'NO'}
                    </Tag>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">Current Balance</Text>
                  <div>
                    <Text strong style={{
                      fontSize: 16,
                      color: viewingAccount.accountType === 'LIABILITY' || viewingAccount.accountType === 'EXPENSE' ?
                             ((viewingAccount.balance || 0) > 0 ? '#ff4d4f' : '#52c41a') :
                             ((viewingAccount.balance || 0) > 0 ? '#52c41a' : '#ff4d4f')
                    }}>
                      ${Math.abs(viewingAccount.balance || 0).toLocaleString()}
                    </Text>
                  </div>
                </div>
              </Col>
              {viewingAccount.parentId && (
                <Col span={24}>
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">Parent Account</Text>
                    <div>
                      <Text>{
                        financialData.accounts.find(a => a.id === viewingAccount.parentId)?.name ||
                        viewingAccount.parentId
                      }</Text>
                    </div>
                  </div>
                </Col>
              )}
              {viewingAccount.note && (
                <Col span={24}>
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">Notes</Text>
                    <div style={{
                      padding: '8px 12px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: 4,
                      marginTop: 8
                    }}>
                      <Text>{viewingAccount.note}</Text>
                    </div>
                  </div>
                </Col>
              )}
              {viewingAccount.createdAt && (
                <Col span={12}>
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">Created At</Text>
                    <div>
                      <Text>{new Date(viewingAccount.createdAt).toLocaleString()}</Text>
                    </div>
                  </div>
                </Col>
              )}
              {viewingAccount.updatedAt && (
                <Col span={12}>
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">Last Updated</Text>
                    <div>
                      <Text>{new Date(viewingAccount.updatedAt).toLocaleString()}</Text>
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AccountingOverview; 